/**
 * Dev-only inspector accordion shown beneath assistant messages.
 *
 * Renders one collapsible block per agent iteration, each containing the
 * tool calls fired in that iteration with their args and a truncated result
 * preview. Visible only when the server has emitted `debug` events, which
 * it gates on Clerk's privateMetadata.role === 'developer'.
 */
import type { DebugInfo } from '../lib/innoq';
import { useT } from '../i18n';

interface DevInspectorProps {
  debug: DebugInfo[];
}

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function DevInspector({ debug }: DevInspectorProps) {
  const t = useT();
  if (!debug || debug.length === 0) return null;

  const totalCalls   = debug.reduce((n, d) => n + d.tool_calls.length, 0);
  const totalModelMs = debug.reduce((n, d) => n + d.model_duration_ms, 0);
  const totalToolMs  = debug.reduce((n, d) => n + d.tool_duration_ms,  0);
  const totalSec     = ((totalModelMs + totalToolMs) / 1000).toFixed(2);

  return (
    <details className="rounded-md border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] mt-1">
      <summary className="cursor-pointer select-none px-2.5 py-1 text-[11px] font-mono text-[var(--text-3)] hover:text-[var(--text-1)] flex items-center gap-2">
        <span>🔍</span>
        <span>{(totalCalls === 1 ? t('devInspector.toolCall', '{count} tool call') : t('devInspector.toolCalls', '{count} tool calls')).replace('{count}', String(totalCalls))} · {totalSec}s</span>
        <span className="text-[var(--text-3)]">· {t('devInspector.iter', '{count} iter').replace('{count}', String(debug.length))}</span>
      </summary>

      <div className="px-2.5 pb-2 space-y-2 border-t border-[var(--border)]">
        {debug.map((iter, idx) => (
          <div key={idx} className="pt-2 first:pt-2">
            <div className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-3)] mb-1">
              {t('devInspector.iterationLine', 'Iter {iteration} · model {modelMs}ms · tools {toolMs}ms')
                .replace('{iteration}', String(iter.iteration))
                .replace('{modelMs}', String(iter.model_duration_ms))
                .replace('{toolMs}', String(iter.tool_duration_ms))}
            </div>
            <div className="space-y-1.5">
              {iter.tool_calls.map((call) => (
                <details key={call.id} className="rounded bg-[var(--surface-2)] border border-[var(--border)]">
                  <summary className="cursor-pointer select-none px-2 py-1 text-[11px] font-mono text-[var(--text-2)] flex items-center gap-2">
                    <span className="font-semibold text-[var(--text-1)]">{call.name}</span>
                    {Object.keys(call.args).length > 0 && (
                      <span className="text-[var(--text-3)]">· {t('devInspector.argsCount', '{count} args').replace('{count}', String(Object.keys(call.args).length))}</span>
                    )}
                  </summary>
                  <div className="px-2 pb-2 space-y-1">
                    <div>
                      <div className="text-[11px] font-mono uppercase text-[var(--text-3)] mt-1">{t('devInspector.args', 'args')}</div>
                      <pre className="text-[10.5px] font-mono whitespace-pre-wrap break-all bg-[var(--surface)] rounded p-1.5 max-h-[120px] overflow-auto custom-scrollbar">
                        {formatJson(call.args)}
                      </pre>
                    </div>
                    <div>
                      <div className="text-[11px] font-mono uppercase text-[var(--text-3)]">{t('devInspector.resultPreview', 'result preview')}</div>
                      <pre className="text-[10.5px] font-mono whitespace-pre-wrap break-all bg-[var(--surface)] rounded p-1.5 max-h-[180px] overflow-auto custom-scrollbar">
                        {call.result_preview}
                      </pre>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}
